import React from "react";
import { motion } from "framer-motion";
import SleekButton from "../../components/ui/NavButtons";

const INVESTOR_TYPES = [
  {
    stage: "Just getting started",
    label: "1 property or less",
    headline: "You don't need to be a finance expert.",
    body: "VaWeNa calculates everything for you — net worth, buying power, property returns. Enter your numbers and see exactly where you stand. No formulas. No finance degree required.",
    features: [
      "Know if you can afford your first rental",
      "See your complete financial picture in minutes",
      "Understand cap rate, DTI, and DSCR — in plain English",
    ],
    cta: "Start with WealthScope — $9.99/mo",
    ctaHref: "https://app.vawena.ai/signup",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    stage: "Growing portfolio",
    label: "2–5 properties",
    headline: "Know exactly which properties are pulling their weight.",
    body: "Tracking multiple rentals across spreadsheets means you always know less than you think. VaWeNa shows all your properties in one view — performance, DSCR, cash flow, and deal readiness.",
    features: [
      "Portfolio performance at a glance",
      "Know which property to refinance, hold, or sell",
      "DSCR lender report when your next deal needs it",
    ],
    cta: "Try the full bundle — $34.99/mo",
    ctaHref: "https://app.vawena.ai/signup",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    highlight: true,
  },
  {
    stage: "Serious investor",
    label: "6+ properties",
    headline: "Get the reports your lender and CPA actually need.",
    body: "When you're managing a real portfolio, data quality is money. VaWeNa generates lender-ready DSCR reports, CPA-ready Schedule E exports, and sends AI alerts before small problems become big ones.",
    features: [
      "Lender-ready DSCR PDF in 60 seconds",
      "Schedule E export — per property, per year",
      "AI alerts when DSCR or cash flow dips below threshold",
    ],
    cta: "Try PortfolioIQ — $29.99/mo",
    ctaHref: "https://app.vawena.ai/signup",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

export const InvestorTypesSection: React.FC = () => {
  return (
    <section className="relative w-full px-4 sm:px-6 py-20 overflow-hidden">
      <div className="absolute inset-0 dark:bg-vw-surface/20 bg-blue-50/30 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent dark:via-blue-500/20 via-blue-300/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent dark:via-blue-500/20 via-blue-300/40 to-transparent" />

      <div className="max-w-[1100px] mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-5">
            Built for every stage
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-4 leading-tight">
            Whether you own one property{" "}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">or twenty.</span>
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-500 max-w-lg mx-auto leading-relaxed">
            VaWeNa grows with you. Start simple. Add complexity only when you need it.
          </p>
        </div>

        {/* Investor type cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {INVESTOR_TYPES.map((type, i) => (
            <motion.div
              key={type.stage}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className={`relative flex flex-col rounded-2xl p-6 border transition-all duration-300 hover:shadow-lg ${
                type.highlight
                  ? "dark:bg-blue-900/30 bg-blue-600 border-blue-500 shadow-lg shadow-blue-500/10"
                  : "dark:bg-white/[0.03] bg-white dark:border-white/[0.08] border-blue-300/80 hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/20 dark:hover:border-blue-500/30 dark:hover:shadow-blue-500/10"
              }`}
            >
              {type.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 rounded-full text-[10px] font-bold uppercase tracking-widest text-white whitespace-nowrap">
                  Most popular
                </div>
              )}

              {/* Stage + label */}
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  type.highlight
                    ? "bg-white/20 text-white"
                    : "dark:bg-blue-500/10 bg-blue-100 dark:text-blue-400 text-blue-600"
                }`}>
                  {type.icon}
                </div>
                <div>
                  <div className={`text-[10px] font-bold uppercase tracking-widest mb-0.5 ${
                    type.highlight ? "text-blue-200" : "dark:text-blue-400 text-blue-600"
                  }`}>{type.stage}</div>
                  <div className={`text-xs font-semibold ${
                    type.highlight ? "text-blue-100" : "dark:text-slate-400 text-gray-400"
                  }`}>{type.label}</div>
                </div>
              </div>

              {/* Headline */}
              <h3 className={`text-base font-bold mb-3 leading-snug ${
                type.highlight ? "text-white" : "dark:text-white text-gray-900"
              }`}>
                {type.headline}
              </h3>

              {/* Body */}
              <p className={`text-sm leading-relaxed mb-5 flex-1 ${
                type.highlight ? "text-blue-100" : "dark:text-slate-400 text-gray-500"
              }`}>
                {type.body}
              </p>

              {/* Feature list */}
              <ul className="space-y-2 mb-6">
                {type.features.map(f => (
                  <li key={f} className="flex items-start gap-2">
                    <svg className={`w-4 h-4 shrink-0 mt-0.5 ${
                      type.highlight ? "text-blue-200" : "text-emerald-400"
                    }`} fill="none" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 7l3.5 3.5 5.5-7" />
                    </svg>
                    <span className={`text-xs leading-snug ${
                      type.highlight ? "text-blue-100" : "dark:text-slate-300 text-gray-600"
                    }`}>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <SleekButton
                variant={type.highlight ? "primary" : "light"}
                className="group !w-full !min-w-0 !justify-center !px-5 !py-2.5 !text-sm"
                onClick={() => window.open(type.ctaHref, "_self")}
              >
                {type.cta}
              </SleekButton>
            </motion.div>
          ))}
        </div>

        {/* Reassurance strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs dark:text-slate-500 text-gray-400"
        >
          {[
            "14-day free trial on all plans",
            "No credit card friction",
            "Upgrade or cancel anytime",
            "Setup in under 10 minutes",
          ].map(t => (
            <span key={t} className="flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full dark:bg-slate-600 bg-gray-300" />
              {t}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
